import { FC, useRef, useEffect, useState, SyntheticEvent } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Button } from '@frontend/design-system';
import { taskCenterTrackingIds } from '../../../tracking-ids';
import { getStatusIconName } from '../../../utils';
import { buttonStyle, containerStyle } from './status-updated.styles';

interface StatusUpdaterProps {
  onAction: (e: SyntheticEvent<HTMLButtonElement>) => void;
  status?: TaskCenterTypes.TaskStatus;
  css?: Interpolation<Theme>;
}

export const StatusUpdater: FC<StatusUpdaterProps> = ({ status, onAction, css }) => {
  const { t } = useTranslation('task-center');
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { name } = getStatusIconName(status);

  const isIncomplete = status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED;
  const isActive = isHovering || isFocused;

  const shouldShowMarkAsDone = isIncomplete && isActive;

  useEffect(() => {
    const onEnter = () => setIsHovering(true);
    const onFocused = () => setIsFocused(true);

    const onLeave = () => setIsHovering(false);
    const onBlurred = () => setIsFocused(false);

    buttonRef.current?.addEventListener('mouseenter', onEnter);
    buttonRef.current?.addEventListener('mouseleave', onLeave);

    buttonRef.current?.addEventListener('focus', onFocused);
    buttonRef.current?.addEventListener('blur', onBlurred);
    return () => {
      buttonRef.current?.removeEventListener('mouseenter', onEnter);
      buttonRef.current?.removeEventListener('mouseleave', onLeave);

      buttonRef.current?.removeEventListener('focus', onFocused);
      buttonRef.current?.removeEventListener('blur', onBlurred);
    };
  }, []);

  return (
    <span ref={buttonRef} css={[containerStyle, css]}>
      {shouldShowMarkAsDone ? (
        <Button
          iconName='mark-done'
          onClick={onAction}
          variant='tertiary'
          css={buttonStyle}
          trackingId={taskCenterTrackingIds.switchTaskFromTaskList}
          hoverLabel={t('Mark as Done')}
        />
      ) : (
        <Icon name={name} size={16} />
      )}
    </span>
  );
};
